import { AbstractControl, ValidationErrors } from '@angular/forms';
import { toDateInputValue } from 'src/app/_helpers/utils';

/**
 * A `yyyy-MM-dd` date no earlier than today. The native `min` only greys the
 * picker: a typed date goes through, and Angular never reads the attribute.
 * Strings of that shape compare as dates; an empty one is `required`'s business.
 */
export function todayOrLater(
  control: AbstractControl
): ValidationErrors | null {
  const value: string = control.value ?? '';
  return value && value < toDateInputValue(new Date())
    ? { pastDate: true }
    : null;
}

/**
 * A meeting with an hour must still be ahead as a whole; with the day alone,
 * `todayOrLater` on it is all there is to check. A group validator: neither
 * control can judge the instant by itself. `yyyy-MM-ddTHH:mm` parses as local
 * time, which is what was typed.
 */
export function meetingInFuture(
  group: AbstractControl
): ValidationErrors | null {
  const day = group.get('meetingDate')?.value;
  const time = group.get('meetingTime')?.value;
  if (!day || !time) return null;
  return new Date(`${day}T${time}`) > new Date() ? null : { pastMeeting: true };
}

/** Rejects a value that is blank once trimmed, which `required` accepts. */
export function nonBlank(control: AbstractControl): ValidationErrors | null {
  return (control.value ?? '').trim() ? null : { required: true };
}

/**
 * `required` for a TinyMCE control: an untouched editor still yields markup
 * such as `<p><br></p>`, so emptiness is judged on the text it renders.
 */
export function richTextRequired(
  control: AbstractControl
): ValidationErrors | null {
  const text = (control.value ?? '')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;|&#160;/gi, '')
    .replace(/ /g, '')
    .trim();
  return text ? null : { required: true };
}

/**
 * At least one document, whichever way it came in: a file picked from disk
 * (`attachments`) or a stored one picked from the search (`existingDocuments`).
 * A group validator, since neither control can demand it alone.
 */
export function documentRequired(
  group: AbstractControl
): ValidationErrors | null {
  const files: unknown[] = group.get('attachments')?.value ?? [];
  const documents: unknown[] = group.get('existingDocuments')?.value ?? [];
  return files.length || documents.length ? null : { documentRequired: true };
}
